import type { DocumentFont } from '@flobi/artwork-core';
import type { CanvasHandle } from '../useEditorCanvas';
import type { OpenDocument } from '../useEditorDocument';
import type { Binding } from '../ai/contracts';
import { fontFinder } from '../../services/fontFinder';
import { readableMediaUrl } from '../../services/mediaRead';
import { addClipBeside, addLayers, addLayersBoard, replaceClip, replacePicture } from './landResult';
import { focusComposite } from './focus';
import { FOCUS_BLUR_BY } from './catalogue';
import type { Reference } from '../ai/contracts';
import { chooseFonts, fontsLine, type LayerDocument } from './document';

/**
 * A finished tool run lands on the layer it was run on, by the tool's landing. One undo step
 * each; a picture traced is hidden, never deleted; a picture split into layers stays as it was,
 * the layers on a new artboard beside the others. Throws with a sentence for the person (the
 * result is saved in My Stuff whatever happens here). Answers what to tell them, when it is
 * more than "done".
 */
export async function landTool(open: OpenDocument, canvas: Pick<CanvasHandle, 'placeVectorOver' | 'placeLayerDocument' | 'revealGenerationTarget'>, binding: Binding, upload?: (blob: Blob, name: string) => Promise<Reference>): Promise<string | void> {
  const tool = binding.tool!, results = binding.results, first = results[0];
  const id = binding.targetId, name = binding.targetName || 'Picture';
  if (!first) throw Error(`${tool.label} answered with nothing to place.`);
  switch (tool.land) {
    case 'replace': replacePicture(open, id, first, false, tool.flattened); return;
    case 'expand': replacePicture(open, id, first, true, tool.flattened); return;
    case 'clip': replaceClip(open, id, first); return;
    case 'layers': {
      const board = addLayersBoard(open, id, results, `${name} layers`, tool.source);
      if (!board) throw Error('The layers could not be placed. They are saved in My Stuff.');
      canvas.revealGenerationTarget(board);
      return `${tool.label} is done. The layers are on a new artboard, “${name} layers”.`;
    }
    case 'cut-out': addLayers(open, id, [first], `${name} subject`, false); return;
    case 'focus': {
      // One picture: the subject sharp over its own background, blurred (focus.ts), as a new take.
      const sent = binding.request.references[0];
      if (!sent || !upload) throw Error('Focus could not find the picture it was made from. The cut-out is saved in My Stuff.');
      const composed = await focusComposite(await readableMediaUrl(sent.url, sent.mediaAssetId), await readableMediaUrl(first.url, first.mediaAssetId), FOCUS_BLUR_BY[tool.strength ?? 'natural']);
      const saved = await upload(composed, `${name} focus.png`);
      if (!saved.mediaAssetId) throw Error('The focused picture could not be saved.');
      replacePicture(open, id, { mediaAssetId: saved.mediaAssetId, url: saved.url, width: saved.width, height: saved.height, mime: saved.mime, name: `${name} focus` }, false, tool.flattened);
      return;
    }
    case 'animate': addClipBeside(open, id, first); return;
    case 'vector': {
      const response = await fetch(await readableMediaUrl(first.url, first.mediaAssetId));
      if (!response.ok) throw Error('The traced picture could not be read. It is saved in My Stuff.');
      const svg = await response.text();
      if (!/<svg[\s>]/i.test(svg)) throw Error('Vectorize answered with a picture, not shapes. It is saved in My Stuff.');
      if (!canvas.placeVectorOver(id, svg, `${name} vector`)) throw Error('The traced picture could not be placed. It is saved in My Stuff.');
      return;
    }
    case 'document': {
      const doc = binding.document as LayerDocument | undefined;
      if (!doc?.layers?.length) throw Error('No layers came back for this picture.');
      // The faces the text is set in, put in the file first so it draws in them at once.
      const finder = await fontFinder(open.documentId);
      const { document, fonts, swapped } = await chooseFonts<DocumentFont>(doc, finder.resolve, finder.preferred);
      const board = canvas.placeLayerDocument(id, document, results, fonts, tool.source);
      if (!board) throw Error('The layers could not be placed. They are saved in My Stuff.');
      canvas.revealGenerationTarget(board);
      return [`${tool.label} is done. The layers are on a new artboard, “${name} layers”.`, fontsLine(swapped)].filter(Boolean).join(' ');
    }
    default: throw Error(`${tool.label} cannot place its result here.`);
  }
}
